/**
 * SMS capability helpers.
 *
 * Product SMS (payments, invoices, estimates, bookings, reviews, polls) is sent
 * from the business's own Twilio number when one is provisioned, and otherwise
 * falls back server-side to the shared GoSite sender number. The UI therefore
 * must not block SMS just because `account_settings.twilio` is empty.
 *
 * Whether the shared fallback is available comes from the backend via
 * `/users/me` as `me.shared_sms_fallback` (derived from the same config the
 * send path falls back to), so UI and backend capability cannot drift. If the
 * backend disables the fallback, the field disappears/turns false and the
 * client-side validation returns automatically.
 *
 * Messenger is the exception: two-way threads need a business-owned number so
 * customer replies route back, so messenger UIs keep the strict gate.
 */

export const hasOwnTwilioNumber = (me) =>
  Boolean(me && me.account_settings && me.account_settings.twilio && me.account_settings.twilio.length)

const isTrialUser = (me) => Boolean(me && me.role_id && me.role_id.role_name === 'trial')

/**
 * Gate for product SMS sends (payments, invoices, estimates, bookings,
 * reviews, polls). True when the account has its own number, is a trial
 * account, or the backend advertises the shared sender-number fallback.
 */
export const canSendProductSms = (me) =>
  hasOwnTwilioNumber(me) || isTrialUser(me) || Boolean(me && me.shared_sms_fallback)

/**
 * Gate for messenger sends: a business-owned number is required for two-way
 * reply routing; the shared-number fallback deliberately does not apply.
 */
export const canSendMessengerSms = (me) => hasOwnTwilioNumber(me)
